/**
 * Shared fetch helper for DM API calls.
 *
 * All requests are sent to the BFF server's /proxy/* routes.
 * HMAC signing happens server-side in server/index.js — the secret key
 * is never shipped to the browser.
 *
 * The browser still generates and persists a clientId (UUID v7) and sends
 * it as x-client-id so the BFF can include it in the HMAC signature.
 *
 * Usage:
 *   await dmRequest(DM_ENV.dmApiBase, '/api/v0/dm/conversations', { accessToken });
 */

import { getClientId } from './clientId';

// BFF proxy base paths (same origin — no secrets in the browser)
const DM_PROXY = '/proxy/dm';
const VERIFICATION_PROXY = '/proxy/verification';
const AUTH_PROXY = '/proxy/auth';
const APP_PLATFORM = process.env.REACT_APP_APP_PLATFORM || 'web-admin';

export const DM_ENV = {
  dmApiBase: DM_PROXY,
  verificationApiBase: VERIFICATION_PROXY,
  authApiBase: AUTH_PROXY,
  appPlatform: APP_PLATFORM,
};

export class ApiError extends Error {
  constructor(message, { status = 0, code = 0, data } = {}) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.data = data;
  }
}

function buildUrl(baseUrl, path, query) {
  if (!baseUrl) {
    throw new Error('dmApiClient: base URL is empty');
  }
  // Allow both absolute URLs (https://...) and BFF proxy paths (/proxy/...)
  if (!/^https?:\/\//i.test(baseUrl) && !baseUrl.startsWith('/')) {
    throw new Error(`dmApiClient: base URL "${baseUrl}" must be an absolute URL or a root-relative path`);
  }
  let url = baseUrl + (path.startsWith('/') ? path : `/${path}`);
  if (query && Object.keys(query).length > 0) {
    const params = new URLSearchParams();
    for (const [k, v] of Object.entries(query)) {
      if (v === undefined || v === null || v === '') continue;
      params.set(k, String(v));
    }
    const qs = params.toString();
    if (qs) url += `?${qs}`;
  }
  return url;
}

async function parseBody(res) {
  const text = await res.text();
  if (!text) return undefined;
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}

/**
 * Send a request via the BFF proxy to a Fancian user-facing backend.
 * HMAC signing is handled server-side; this client only passes the clientId.
 *
 * @param {string} baseUrl  BFF proxy base path (e.g. DM_ENV.dmApiBase)
 * @param {string} path     API path beginning with /
 * @param {object} options
 * @param {('GET'|'POST'|'PUT'|'PATCH'|'DELETE')=} options.method  default GET
 * @param {Record<string, any>=} options.query
 * @param {any=} options.body            JSON body, will be stringified
 * @param {string=} options.accessToken  bearer token
 * @param {Record<string, string>=} options.extraHeaders
 */
export async function dmRequest(baseUrl, path, options = {}) {
  const {
    method = 'GET',
    query,
    body,
    accessToken,
    extraHeaders = {},
  } = options;

  const url = buildUrl(baseUrl, path, query);

  const headers = {
    Accept: 'application/json',
    // Pass clientId so the BFF can include it in the server-side HMAC signature
    'x-client-id': getClientId(),
    ...extraHeaders,
  };
  if (body !== undefined) {
    headers['Content-Type'] = 'application/json';
  }
  if (accessToken) {
    headers.Authorization = `Bearer ${accessToken}`;
  }
  if (APP_PLATFORM) {
    headers['x-app-platform'] = APP_PLATFORM;
  }

  let res;
  try {
    res = await fetch(url, {
      method,
      headers,
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });
  } catch (err) {
    throw new ApiError(err?.message || 'Network error', { status: 0 });
  }

  const data = await parseBody(res);
  if (!res.ok) {
    const code =
      (data && typeof data === 'object' && typeof data.code === 'number'
        ? data.code
        : 0) || 0;
    let msg =
      (data && typeof data === 'object' && typeof data.msg === 'string' && data.msg) ||
      (data && typeof data === 'object' && typeof data.message === 'string' && data.message) ||
      `HTTP ${res.status}`;
    // Backends may include a `detail` field in the TEST environment only — surface
    // it in the error message to aid debugging (never present in prod responses).
    if (data && typeof data === 'object' && typeof data.detail === 'string' && data.detail) {
      msg = `${msg} — ${data.detail}`;
    }
    throw new ApiError(msg, { status: res.status, code, data });
  }
  return data;
}
